import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { AlignDialog } from '@/components/align'
import { FxAlert, FxButton, FxIcon, FxPanel, FxSpinner } from '@/components/fx'
import type { EvaluationDocument, Job, Template } from '@/types/domain'

import { CockpitDialog } from './CockpitDialog'
import {
  bestPageOf,
  documentCountSubject,
  formatClockTime,
  formatSimilarity,
  formatUploadDate,
  noMatchReasonOf,
} from './documentsModel'
import { layoutLabel } from '../useCockpit'
import { MoveDocumentDialog } from './MoveDocumentDialog'
import { documentActionError, type MoveInput } from './useDocuments'

/**
 * "Problemfälle": the documents no template page fit, and the jobs that
 * failed outright.
 *
 * A no-match is not a bug report, it is a decision to take, so every row
 * carries what the matcher measured (its best page, the similarity, the
 * German reason the gateway derived from `matchMetrics.reasons`) and the four
 * ways out: force that page, mark the corners by hand, move the scan to the
 * template it actually belongs to, or discard it.
 *
 * **Cancelled jobs are not problems.** `isJobFailure` is `failed` only; a
 * cancelled job carries an `error` of "Cancelled" because the gateway records
 * why it stopped, and listing that here would report a problem for every
 * deliberate stop — which is exactly how a problem list stops being read.
 */
export function ProblemCases({
  templateId,
  template,
  documents,
  noMatchTotal,
  failedJobs,
  isLoading,
  canWrite,
  onForcePage,
  onReanalyse,
  onDiscard,
  onMove,
  actionPending,
  actionError,
  moveError,
  movePending,
}: {
  templateId: string
  template?: Template
  documents: EvaluationDocument[]
  noMatchTotal: number
  failedJobs: Job[]
  isLoading: boolean
  canWrite: boolean
  onForcePage: (documentId: string, page: number) => void
  onReanalyse: (documentIds: string[]) => void
  onDiscard: (documentId: string) => void
  onMove: (input: MoveInput) => void
  actionPending: boolean
  actionError: unknown
  moveError: unknown
  movePending: boolean
}) {
  const queryClient = useQueryClient()
  const [discarding, setDiscarding] = useState<EvaluationDocument | null>(null)
  const [moving, setMoving] = useState<EvaluationDocument | null>(null)
  const align = useAlignParam()
  const aligning = documents.find((doc) => doc.id === align.documentId) ?? null

  if (noMatchTotal === 0 && failedJobs.length === 0) return null

  return (
    <FxPanel accent="danger" className="p-6 sm:px-8 sm:py-6">
      <header className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-panel-title">Problemfälle</h2>
        {noMatchTotal > 0 && (
          <span className="rounded-sm bg-danger-subtle px-2 py-0.5 text-small text-text">
            {noMatchTotal} ohne Treffer
          </span>
        )}
        {failedJobs.length > 0 && (
          <span className="rounded-sm bg-danger-subtle px-2 py-0.5 text-small text-text">
            {failedJobs.length === 1 ? '1 Auftrag' : `${failedJobs.length} Aufträge`}{' '}
            fehlgeschlagen
          </span>
        )}
      </header>

      {actionError != null && (
        <FxAlert tone="danger" className="mb-4">
          {documentActionError(actionError)}
        </FxAlert>
      )}

      {isLoading && documents.length === 0 ? (
        <FxSpinner label="Problemfälle werden geladen …" />
      ) : (
        <ul className="m-0 list-none p-0">
          {documents.map((doc) => (
            <NoMatchRow
              key={doc.id}
              template={template}
              doc={doc}
              canWrite={canWrite}
              pending={actionPending}
              onForcePage={onForcePage}
              onMove={() => setMoving(doc)}
              onDiscard={() => setDiscarding(doc)}
              onAlign={() => align.open(doc.id)}
            />
          ))}
          {failedJobs.map((job) => (
            <li key={job.id} className="border-b-2 border-divider py-4 last:border-b-0">
              <p className="m-0 text-body-strong">
                {documentCountSubject(job.document_ids?.length ?? 0)} konnten nicht analysiert
                werden
              </p>
              <p className="m-0 mt-1 text-small text-danger-text">
                {job.error || 'Der Auftrag ist ohne Meldung fehlgeschlagen.'}
              </p>
              {canWrite && (
                <FxButton
                  variant="tonal"
                  className="mt-3"
                  disabled={actionPending || (job.document_ids?.length ?? 0) === 0}
                  onClick={() => onReanalyse(job.document_ids ?? [])}
                >
                  Erneut analysieren
                </FxButton>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Only the first few are shown; the filter is where the rest live. */}
      {noMatchTotal > documents.length && (
        <p className="m-0 mt-4 text-small text-text-muted">
          {documents.length} von {noMatchTotal} ohne Treffer angezeigt — die übrigen finden Sie
          unter dem Filter „Kein Treffer“.
        </p>
      )}

      <CockpitDialog
        open={discarding != null}
        onOpenChange={(open) => !open && setDiscarding(null)}
        title="Dokument verwerfen?"
        description={
          <>„{discarding?.title}“ wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.</>
        }
        footer={
          <>
            <FxButton variant="outlined" onClick={() => setDiscarding(null)}>
              Abbrechen
            </FxButton>
            <FxButton
              variant="danger"
              disabled={actionPending}
              onClick={() => {
                if (discarding) onDiscard(discarding.id)
                setDiscarding(null)
              }}
            >
              Verwerfen
            </FxButton>
          </>
        }
      />

      <MoveDocumentDialog
        open={moving != null}
        onOpenChange={(open) => !open && setMoving(null)}
        documentTitle={moving?.title ?? ''}
        currentTemplateId={templateId}
        pending={movePending}
        error={moveError}
        onSubmit={(input) => {
          if (!moving) return
          onMove({ ...input, documentId: moving.id })
        }}
      />

      {/* "Ausrichtung korrigieren" is a dialog over this screen, not a route
          of its own — the person stays in the list they are working. It opens
          on `?dialog=align&doc=<id>`, the convention the dialog's own
          docstring names and the review screen uses as well, so one address
          opens it from either screen and a reload does not lose it. */}
      {aligning && (
        <AlignDialog
          templateId={templateId}
          document={aligning}
          layouts={template?.pageList ?? []}
          onClose={align.close}
          onStarted={() => {
            align.close()
            void queryClient.invalidateQueries({ queryKey: ['documents'] })
            void queryClient.invalidateQueries({ queryKey: ['jobs'] })
          }}
        />
      )}
    </FxPanel>
  )
}

/**
 * `?dialog=align&doc=<documentId>`, read and written here rather than through
 * `useCockpitDialog`, which carries the dialog name alone — this one needs to
 * say *which* document as well.
 *
 * `replace`, like every other dialog in the cockpit: opening and closing
 * would otherwise fill the history with entries that look identical.
 */
function useAlignParam() {
  const [params, setParams] = useSearchParams()
  const documentId = params.get('dialog') === 'align' ? params.get('doc') : null

  const open = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params)
      next.set('dialog', 'align')
      next.set('doc', id)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const close = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('dialog')
    next.delete('doc')
    setParams(next, { replace: true })
  }, [params, setParams])

  return { documentId, open, close }
}

function NoMatchRow({
  template,
  doc,
  canWrite,
  pending,
  onForcePage,
  onMove,
  onDiscard,
  onAlign,
}: {
  template?: Template
  doc: EvaluationDocument
  canWrite: boolean
  pending: boolean
  onForcePage: (documentId: string, page: number) => void
  onMove: () => void
  onDiscard: () => void
  onAlign: () => void
}) {
  const bestPage = bestPageOf(doc.matchMetrics)
  const similarity = formatSimilarity(doc.matchMetrics?.similarity)
  // The day, not just the clock: no-match documents sit here until somebody
  // clears them, so a bare "14:32 Uhr" names a day the reader cannot know —
  // and reads as today. Same wording as the table on this tab.
  const uploadedDay = formatUploadDate(doc.uploadedAt)
  const uploadedTime = formatClockTime(doc.uploadedAt)
  const uploaded = uploadedTime ? `${uploadedDay}, ${uploadedTime} Uhr` : null

  return (
    <li className="border-b-2 border-divider py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <FxIcon name="cancel" size={22} className="shrink-0 text-danger-text" />
        <b className="min-w-0 break-all text-body-strong">{doc.title}</b>
        {uploaded && <span className="text-small text-text-muted">hochgeladen {uploaded}</span>}
        <span className="flex-1" />
        {(bestPage != null || similarity != null) && (
          <span className="rounded-sm bg-fill px-2 py-0.5 text-small">
            {bestPage != null ? `beste Seite: ${layoutLabel(template, bestPage)}` : 'keine Seite'}
            {similarity != null && ` · Ähnlichkeit ${similarity}`}
          </span>
        )}
      </div>
      <p className="m-0 ml-[34px] mt-1 text-small">{noMatchReasonOf(doc)}</p>

      {canWrite && (
        <div className="ml-[34px] mt-3 flex flex-wrap items-center gap-3">
          <FxButton variant="tonal" icon="crop_rotate" disabled={pending} onClick={onAlign}>
            Ausrichtung korrigieren
          </FxButton>
          {bestPage != null && (
            <FxButton
              variant="tonal"
              disabled={pending}
              onClick={() => onForcePage(doc.id, bestPage)}
            >
              {layoutLabel(template, bestPage)} trotzdem zuordnen
            </FxButton>
          )}
          <FxButton variant="tonal" icon="drive_file_move" disabled={pending} onClick={onMove}>
            Andere Vorlage
          </FxButton>
          <span className="flex-1" />
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-small font-semibold text-danger-text"
            disabled={pending}
            onClick={onDiscard}
          >
            <FxIcon name="delete" size={18} />
            Verwerfen
          </button>
        </div>
      )}
    </li>
  )
}
